/**
 * =============================================================================
 * MetricCard — B-6 shared primitive (ADR-025 §6.2; design-v3 §F)
 * =============================================================================
 *
 * EXTRACTED byte-for-byte from the B-3 Overview pending-work tile
 * (OverviewPanel.tsx) — the named incumbent per the frozen blueprint. The tile's
 * tri-state honesty semantics stay at the caller (it decides value vs '—',
 * the freshness chip variant/label, and the meta lines); this primitive owns
 * only the shared presentational skeleton:
 *
 *   Panel(header) > metric Text > unit Text > StatusChip > [age Text] >
 *   [asOf Text] > action slot
 *
 * NOT adopted by AnalyticsDashboard's stat tiles (different markup family —
 * centered Box; adopting would visibly change Analytics) — design-v3 §F.
 * =============================================================================
 */

import React from 'react';
import { Panel, Text } from '@bigcommerce/big-design';
import { StatusChip, type StatusChipVariant } from './StatusChip';

export interface MetricCardProps {
  /** Panel header (the tile title). */
  title: string;
  /** Rendered metric — the caller resolves tri-state ('—' when unavailable). */
  value: React.ReactNode;
  /** Unit line under the metric (e.g. "questions"). */
  unit: string;
  /** Freshness chip (tri-state semantics computed by the caller). */
  chip: { variant: StatusChipVariant; label: string };
  /** Optional meta line (e.g. oldest-item age) — rendered only when non-null. */
  ageLine?: string | null;
  /** Optional as-of line — rendered only when non-null. */
  asOfLine?: string | null;
  /** Action slot (e.g. the subtle "Open filtered queue" button). */
  action: React.ReactNode;
  testId?: string;
}

export function MetricCard({
  title,
  value,
  unit,
  chip,
  ageLine,
  asOfLine,
  action,
  testId,
}: MetricCardProps): React.ReactElement {
  return (
    <Panel header={title} data-testid={testId}>
      <Text bold marginBottom="xxSmall">
        {value}
      </Text>
      <Text color="secondary" marginBottom="xSmall">
        {unit}
      </Text>
      <StatusChip variant={chip.variant} label={chip.label} />
      {ageLine != null && (
        <Text color="secondary" marginTop="xSmall" marginBottom="none">
          {ageLine}
        </Text>
      )}
      {asOfLine != null && (
        <Text color="secondary" marginBottom="small">
          {asOfLine}
        </Text>
      )}
      {action}
    </Panel>
  );
}
